import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";
import ImageModal from "../components/ImageModal";
import LoadingSpinner from "../components/LoadingSpinner";
import CreateIssueForm, { IssueCreatedBanner } from "../components/CreateIssueForm";
import { useAuth } from "../contexts/AuthContext";

const severityColor = {
    critical: "var(--red)",
    high: "#f97316",
    warning: "#eab308",
    medium: "#3b82f6",
    low: "var(--text-muted)",
    info: "var(--text-muted)",
};

function ScoreBadge({ score }) {
    if (score == null) return <span style={{ color: "var(--text-muted)" }}>--</span>;
    const color = score >= 8 ? "#22c55e" : score >= 5 ? "#eab308" : "var(--red)";
    return <span style={{ color, fontWeight: 600 }}>{score}/10</span>;
}

function Reports() {
    const { accounts } = useMsal();
    const currentAccount = accounts[0];
    const { hasPermission } = useAuth();
    const canCreateIssue = hasPermission('issues.create');
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSite, setSelectedSite] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [expandedScan, setExpandedScan] = useState(null);
    const [scanDetail, setScanDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);
    const [siteSearch, setSiteSearch] = useState("");
    // Key format: "finding:123" or "preset:456"
    const [creatingIssueFor, setCreatingIssueFor] = useState(null);
    const [issueResults, setIssueResults] = useState({}); // key → result
    // Review tracking
    const [activeReviewKey, setActiveReviewKey] = useState(null); // "siteId/scanHour"
    const [reviewIssueCount, setReviewIssueCount] = useState(0);
    const [reviewCompleting, setReviewCompleting] = useState(false);

    useEffect(() => {
        api.scans()
            .then(data => { setScans(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    // Derive unique sites from scans
    const sites = useMemo(() => {
        const siteMap = {};
        for (const scan of scans) {
            if (!siteMap[scan.siteId]) {
                siteMap[scan.siteId] = {
                    siteId: scan.siteId,
                    storeName: scan.storeName,
                    scanCount: 0,
                    latestScan: null,
                    totalScore: 0,
                    scoreCount: 0,
                };
            }
            const site = siteMap[scan.siteId];
            site.scanCount++;
            if (!site.latestScan || scan.scanStart > site.latestScan) {
                site.latestScan = scan.scanStart;
            }
            if (scan.avgScore != null) {
                site.totalScore += scan.avgScore;
                site.scoreCount++;
            }
        }
        return Object.values(siteMap)
            .map(s => ({ ...s, avgScore: s.scoreCount > 0 ? Math.round(s.totalScore / s.scoreCount) : null }))
            .sort((a, b) => (b.latestScan || "").localeCompare(a.latestScan || ""));
    }, [scans]);

    // Scans for selected site, grouped by date
    const siteDates = useMemo(() => {
        if (!selectedSite) return [];
        const dateMap = {};
        for (const scan of scans) {
            if (scan.siteId !== selectedSite.siteId) continue;
            const date = scan.scanHour?.slice(0, 10);
            if (!date) continue;
            if (!dateMap[date]) {
                dateMap[date] = { date, scans: [], totalFindings: 0, criticalCount: 0 };
            }
            dateMap[date].scans.push(scan);
            dateMap[date].totalFindings += scan.findingCount || 0;
            dateMap[date].criticalCount += scan.criticalCount || 0;
        }
        return Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date));
    }, [scans, selectedSite]);

    // Scans for selected site + date
    const dateScans = useMemo(() => {
        if (!selectedDate) return [];
        return siteDates.find(d => d.date === selectedDate)?.scans || [];
    }, [siteDates, selectedDate]);

    const handleExpandScan = useCallback(async (scan) => {
        const key = `${scan.siteId}/${scan.scanHour}`;
        if (expandedScan === key) {
            setExpandedScan(null);
            setScanDetail(null);
            return;
        }
        setExpandedScan(key);
        setScanDetail(null);
        setDetailLoading(true);
        try {
            const hourParam = scan.scanHour.replace(' ', '+');
            const data = await api.scanDetail(scan.siteId, hourParam);
            setScanDetail(data);
        } catch (err) {
            console.error("Scan detail error:", err);
        } finally {
            setDetailLoading(false);
        }
    }, [expandedScan]);

    const formatTime = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleString([], { hour: "2-digit", minute: "2-digit" });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr + "T00:00:00");
        return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    };

    const handleIssueCreated = (key, result) => {
        setIssueResults(prev => ({ ...prev, [key]: result }));
        setCreatingIssueFor(null);
        // Track issues raised during active review session
        if (activeReviewKey) {
            setReviewIssueCount(prev => prev + 1);
        }
    };

    const handleStartReview = async (scan) => {
        const hourParam = scan.scanHour.replace(' ', '+');
        const key = `${scan.siteId}/${scan.scanHour}`;
        try {
            await api.startReview(scan.siteId, hourParam, {
                reviewerEmail: currentAccount?.username || 'web_user',
                reviewerName: currentAccount?.name || currentAccount?.username || 'Web User'
            });
            setActiveReviewKey(key);
            setReviewIssueCount(0);
        } catch (err) {
            console.error('Failed to start review:', err);
        }
    };

    const handleCompleteReview = async (scan) => {
        const hourParam = scan.scanHour.replace(' ', '+');
        setReviewCompleting(true);
        try {
            await api.completeReview(scan.siteId, hourParam, {
                reviewerEmail: currentAccount?.username || 'web_user',
                issuesRaised: reviewIssueCount
            });
            setActiveReviewKey(null);
            setReviewIssueCount(0);
            // Update local scan data to reflect review
            setScans(prev => prev.map(s =>
                s.siteId === scan.siteId && s.scanHour === scan.scanHour
                    ? { ...s, reviewStatus: 'completed', reviewerName: currentAccount?.name || currentAccount?.username }
                    : s
            ));
        } catch (err) {
            console.error('Failed to complete review:', err);
        } finally {
            setReviewCompleting(false);
        }
    };

    if (loading) return <LoadingSpinner message="Loading scan reports..." />;

    // ==================== STEP 1: Site Selector ====================
    if (!selectedSite) {
        const q = siteSearch.toLowerCase();
        const filteredSites = q
            ? sites.filter(s => String(s.siteId).includes(q) || (s.storeName || "").toLowerCase().includes(q))
            : sites;

        return (
            <div>
                <div className="page-header">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                        <div>
                            <h2>Scan Reports</h2>
                            <p>{sites.length} site{sites.length !== 1 ? "s" : ""} with scans</p>
                        </div>
                        <input
                            type="text"
                            className="filter-select"
                            placeholder="Search by site ID or name..."
                            value={siteSearch}
                            onChange={(e) => setSiteSearch(e.target.value)}
                            style={{ minWidth: 220, fontSize: 14, padding: "8px 14px" }}
                        />
                    </div>
                </div>

                {filteredSites.length === 0 ? (
                    <div className="empty-state">
                        <h3>No sites found</h3>
                        <p>No sites match your search.</p>
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
                        {filteredSites.map(site => (
                            <div
                                key={site.siteId}
                                className="card"
                                style={{ cursor: "pointer", padding: 16, transition: "border-color 0.15s" }}
                                onClick={() => { setSelectedSite(site); setSelectedDate(null); }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--primary)"}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = ""}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>{site.storeName}</div>
                                        <div style={{ color: "var(--text-muted)", fontSize: 13 }}>#{site.siteId}</div>
                                    </div>
                                    <ScoreBadge score={site.avgScore} />
                                </div>
                                <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 13, color: "var(--text-secondary)" }}>
                                    <span>{site.scanCount} scan{site.scanCount !== 1 ? "s" : ""}</span>
                                    {site.latestScan && (
                                        <span>Latest: {new Date(site.latestScan).toLocaleDateString()}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ==================== STEP 2: Date Bar + Step 3: Scans ====================
    return (
        <div>
            {/* Breadcrumb */}
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 13, color: "var(--text-muted)" }}>
                    <span style={{ cursor: "pointer", color: "var(--primary)" }} onClick={() => { setSelectedSite(null); setSelectedDate(null); }}>
                        Reports
                    </span>
                    <span>&rsaquo;</span>
                    <span>{selectedSite.storeName} #{selectedSite.siteId}</span>
                    {selectedDate && (
                        <>
                            <span>&rsaquo;</span>
                            <span>{formatDate(selectedDate)}</span>
                        </>
                    )}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h2>{selectedSite.storeName}</h2>
                    <button
                        className="action-cancel"
                        onClick={() => { setSelectedSite(null); setSelectedDate(null); }}
                    >
                        Back to Sites
                    </button>
                </div>
            </div>

            {/* Date buttons */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                {siteDates.map(d => (
                    <button
                        key={d.date}
                        onClick={() => { setSelectedDate(d.date === selectedDate ? null : d.date); setExpandedScan(null); setScanDetail(null); }}
                        style={{
                            padding: "6px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer",
                            border: "1px solid " + (selectedDate === d.date ? "var(--primary)" : "var(--border)"),
                            borderRadius: 6,
                            background: selectedDate === d.date ? "var(--primary)" : "var(--bg-secondary)",
                            color: selectedDate === d.date ? "#fff" : "var(--text-primary)",
                        }}
                    >
                        {formatDate(d.date)}
                        <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 12 }}>
                            {d.scans.length} scan{d.scans.length !== 1 ? "s" : ""}
                        </span>
                        {d.totalFindings > 0 && (
                            <span style={{
                                marginLeft: 6, fontSize: 11, fontWeight: 700,
                                color: d.criticalCount > 0 ? "var(--red)" : "#f97316",
                                background: selectedDate === d.date ? "rgba(255,255,255,0.2)" : "rgba(249,115,22,0.1)",
                                padding: "1px 6px", borderRadius: 10
                            }}>
                                {d.totalFindings}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* If no date selected, show summary */}
            {!selectedDate ? (
                <div className="empty-state">
                    <h3>Select a date above</h3>
                    <p>{siteDates.length} day{siteDates.length !== 1 ? "s" : ""} with scans for this site.</p>
                </div>
            ) : (
                /* Scan cards for the selected date */
                <div className="scan-list">
                    {dateScans.map((scan) => {
                        const key = `${scan.siteId}/${scan.scanHour}`;
                        const isExpanded = expandedScan === key;
                        return (
                            <div key={key} className={`scan-row ${isExpanded ? "expanded" : ""}`}>
                                <div className="scan-row-header" onClick={() => handleExpandScan(scan)}>
                                    <div className="scan-row-main">
                                        <span className="scan-store-name">{formatTime(scan.scanStart)}</span>
                                    </div>
                                    <div className="scan-row-meta">
                                        <span className="scan-presets">{scan.presetCount} presets</span>
                                        <ScoreBadge score={scan.avgScore} />
                                        {scan.findingCount > 0 && (
                                            <span className="scan-findings-badge" style={{
                                                color: scan.criticalCount > 0 ? "var(--red)" : "#f97316"
                                            }}>
                                                {scan.findingCount} finding{scan.findingCount !== 1 ? "s" : ""}
                                            </span>
                                        )}
                                        {scan.reviewStatus === 'completed' && (
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                                                background: "rgba(34,197,94,0.1)", color: "#22c55e"
                                            }}>
                                                Reviewed{scan.reviewerName ? ` by ${scan.reviewerName}` : ""}
                                            </span>
                                        )}
                                        {scan.reviewStatus === 'in_progress' && (
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                                                background: "rgba(59,130,246,0.1)", color: "#3b82f6"
                                            }}>
                                                Under review{scan.reviewerName ? ` by ${scan.reviewerName}` : ""}
                                            </span>
                                        )}
                                        <span className="scan-expand-icon">{isExpanded ? "\u25B2" : "\u25BC"}</span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="scan-detail">
                                        {/* Review action bar */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                                            {activeReviewKey === key ? (
                                                <>
                                                    <span style={{ fontSize: 13, color: "#3b82f6", fontWeight: 600 }}>
                                                        Reviewing — {reviewIssueCount} issue{reviewIssueCount !== 1 ? "s" : ""} raised
                                                    </span>
                                                    <button
                                                        className="action-submit"
                                                        style={{ fontSize: 13, padding: "4px 14px" }}
                                                        onClick={(e) => { e.stopPropagation(); handleCompleteReview(scan); }}
                                                        disabled={reviewCompleting}
                                                    >
                                                        {reviewCompleting ? "Completing..." : "Mark as Reviewed"}
                                                    </button>
                                                </>
                                            ) : scan.reviewStatus !== 'completed' ? (
                                                <button
                                                    className="raise-issue-btn raise-issue-btn-primary"
                                                    style={{ fontSize: 13, padding: "4px 14px" }}
                                                    onClick={(e) => { e.stopPropagation(); handleStartReview(scan); }}
                                                >
                                                    I am reviewing this report
                                                </button>
                                            ) : (
                                                <span style={{ fontSize: 13, color: "#22c55e", fontWeight: 500 }}>
                                                    Reviewed{scan.reviewerName ? ` by ${scan.reviewerName}` : ""}
                                                </span>
                                            )}
                                        </div>

                                        {detailLoading ? (
                                            <LoadingSpinner message="Loading scan details..." />
                                        ) : scanDetail ? (
                                            <div className="scan-preset-grid">
                                                {scanDetail.presets.map((p) => {
                                                    const presetKey = `preset:${p.id}`;
                                                    return (
                                                    <div key={p.id} className="scan-preset-card">
                                                        {p.imageUrl ? (
                                                            <img
                                                                src={p.imageUrl}
                                                                alt={p.presetName}
                                                                className="scan-preset-image"
                                                                onClick={() => {
                                                                    const allImages = scanDetail.presets
                                                                        .filter(x => x.imageUrl)
                                                                        .map(x => ({ url: x.imageUrl, label: `${x.presetName} - ${x.checkType}` }));
                                                                    const idx = allImages.findIndex(x => x.url === p.imageUrl);
                                                                    setModalImages(allImages);
                                                                    setModalIndex(idx >= 0 ? idx : 0);
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="scan-preset-no-image">No image</div>
                                                        )}
                                                        <div className="scan-preset-info">
                                                            <div className="scan-preset-header">
                                                                <strong>{p.presetName}</strong>
                                                                <ScoreBadge score={p.score} />
                                                            </div>
                                                            <div className="scan-preset-type">
                                                                {p.checkType}
                                                                {p.humanIssueCount > 0 && (
                                                                    <span style={{
                                                                        marginLeft: 8, fontSize: 11, fontWeight: 600,
                                                                        color: "var(--text-muted)", background: "var(--bg-secondary)",
                                                                        padding: "1px 6px", borderRadius: 10
                                                                    }}>
                                                                        {p.humanIssueCount} past issue{p.humanIssueCount !== 1 ? "s" : ""}
                                                                    </span>
                                                                )}
                                                                {p.openIssueCount > 0 && (
                                                                    <span style={{
                                                                        marginLeft: 4, fontSize: 11, fontWeight: 700,
                                                                        color: "var(--red)", background: "rgba(239,68,68,0.1)",
                                                                        padding: "1px 6px", borderRadius: 10
                                                                    }}>
                                                                        {p.openIssueCount} open
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {p.summary && (
                                                                <div className="scan-preset-summary">{p.summary}</div>
                                                            )}

                                                            {/* Findings (if any) with per-finding Create Issue */}
                                                            {p.findings.length > 0 && (
                                                                <div className="scan-preset-findings">
                                                                    {p.findings.map((f) => {
                                                                        const fKey = `finding:${f.id}`;
                                                                        return (
                                                                        <div key={f.id} className="scan-finding-item" style={{ marginBottom: 8 }}>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                                <span className="scan-finding-severity" style={{
                                                                                    color: severityColor[f.severity] || "var(--text-secondary)"
                                                                                }}>
                                                                                    {f.severity}
                                                                                </span>
                                                                                <span className="scan-finding-title">{f.title}</span>
                                                                            </div>
                                                                            {f.description && (
                                                                                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                                                                                    {f.description}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}

                                                            {/* Preset-level Create Issue button — always visible */}
                                                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                                                                {issueResults[presetKey] ? (
                                                                    <IssueCreatedBanner result={issueResults[presetKey]} />
                                                                ) : creatingIssueFor === presetKey ? (
                                                                    <CreateIssueForm
                                                                        preset={p}
                                                                        siteId={selectedSite.siteId}
                                                                        scanHour={scan.scanHour}
                                                                        onCreated={(result) => handleIssueCreated(presetKey, result)}
                                                                        onCancel={() => setCreatingIssueFor(null)}
                                                                    />
                                                                ) : canCreateIssue ? (
                                                                    <button
                                                                        className="raise-issue-btn raise-issue-btn-primary"
                                                                        onClick={(e) => { e.stopPropagation(); setCreatingIssueFor(presetKey); }}
                                                                    >
                                                                        Raise Issue
                                                                    </button>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="empty-state"><p>Failed to load details</p></div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

export default Reports;
